import { Form, Formik, FormikHelpers } from 'formik'
import { NextPage } from 'next'
import { Button } from '~/components/button'
import { Link } from '~/components/link'
import { TextInputField } from '~/components/text-input'
import { Meta } from '~/components/typography'
import { routes } from '~/config/routes'
import { useRecaptcha } from '~/hooks/use-recaptcha'
import { useUrlQuery } from '~/hooks/use-url-query'
import { CenterLayout } from '~/layouts/center-layout'
import { colors } from '~/styles/colors'
import { apiError } from '~/utils/api-error'

import IconEmail from './assets/icon-email.svg'
import { useDonorVerifyEmailMutation } from './graphql/donor-verify-email'
import { useForm, Values } from './hooks/use-form'

export const DonorPortalSignIn: NextPage = () => {
  const email = useUrlQuery('email')

  const recaptcha = useRecaptcha()
  const [donorVerifyEmail, { data: donorVerifyEmailData }] =
    useDonorVerifyEmailMutation()

  const { initialValues, schema } = useForm(email)

  if (donorVerifyEmailData?.donorVerifyEmail.donorSignInEmailSent) {
    return (
      <CenterLayout
        icon={<IconEmail />}
        heading="Check Your Email"
        subheading="To manage your donation please confirm your login to the donor portal by clicking the link we’ve just sent to your email inbox."
      />
    )
  }

  const handleSubmit = async (
    values: Values,
    helpers: FormikHelpers<Values>
  ) => {
    try {
      const recaptchaResponse = await recaptcha.execute()
      await donorVerifyEmail({
        variables: {
          input: {
            email: values.email,
            recaptchaResponse,
          },
        },
      })
    } catch (error: any) {
      recaptcha.reset()
      apiError(error, {
        invalid_email_password: () => {
          helpers.setFieldError('email', 'Email does not exist')
        },
      })
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
      validateOnBlur={false}
    >
      {({ isSubmitting }) => (
        <CenterLayout
          heading="Sign In to Donor Portal"
          subheading="Please enter your email."
          maxWidth={448}
          headerLink={
            <Link href={routes.signIn}>
              <Meta as="a" color={colors.slateGray}>
                Nonprofit Sign In
              </Meta>
            </Link>
          }
        >
          <Form>
            <TextInputField
              label="Email"
              name="email"
              autoComplete="email"
              large
              marginBottom={32}
            />

            <Button
              type="submit"
              loading={isSubmitting}
              size="large"
              width="100%"
            >
              Continue
            </Button>
          </Form>
        </CenterLayout>
      )}
    </Formik>
  )
}
